import React, { useState } from 'react';
import { Calendar, MapPin, Target, FileText, Sparkles, Loader2, CheckCircle2, AlertCircle, Globe2 } from 'lucide-react';
import { t } from '../services/i18n';
import { generateSeoStrategy, generateArticleFromBrief } from '../services/aiService';
import { User } from '../types';

interface SeoStrategyPlannerProps {
  currentUser: User;
}

interface StrategyPost {
  id: string;
  week: number;
  day: string;
  date_label: string;
  title: string;
  target_keyword: string;
  content_type: string;
}

interface StrategyResponse {
  strategy_overview: string;
  suggested_keywords: string[];
  posting_frequency: string;
  total_weeks: number;
  max_articles_per_week: number;
  plan_type: 'free' | 'premium';
  calendar: Array<{
    week: number;
    focus: string;
    posts: StrategyPost[];
  }>;
}

interface GeneratedArticle {
  id: string;
  title: string;
  main_keyphrase?: string;
  seo_title?: string;
  meta_description?: string;
  slug?: string;
  target_keyword: string;
  content: string;
  language: string;
  credits_used: number;
  keyword_density?: string;
  image_suggestions?: Array<{ alt_text: string; description: string; position: string }>;
}

export const SeoStrategyPlanner: React.FC<SeoStrategyPlannerProps> = ({ currentUser }) => {
  const [topic, setTopic] = useState('');
  const [weeks, setWeeks] = useState(4);
  const [loadingStrategy, setLoadingStrategy] = useState(false);
  const [strategyError, setStrategyError] = useState<string | null>(null);
  const [strategy, setStrategy] = useState<StrategyResponse | null>(null);

  const [generatingArticleId, setGeneratingArticleId] = useState<string | null>(null);
  const [articles, setArticles] = useState<Record<string, GeneratedArticle>>({});
  const [articleError, setArticleError] = useState<string | null>(null);

  const isPremium = currentUser.plan !== 'free' && !!currentUser.subscriptionExpiry && new Date(currentUser.subscriptionExpiry) > new Date();

  const handleGenerateStrategy = async () => {
    if (!topic.trim()) {
      setStrategyError(t('strategy.errorTopicRequired') || 'Please enter a topic or niche.');
      return;
    }
    setStrategyError(null);
    setStrategy(null);
    setArticles({});
    setLoadingStrategy(true);
    try {
      const res = await generateSeoStrategy(topic.trim(), weeks);
      setStrategy(res);
    } catch (err: any) {
      console.error('SEO strategy error:', err);
      setStrategyError(err.message || (t('strategy.errorGeneric') || 'Failed to generate strategy. Please try again.'));
    } finally {
      setLoadingStrategy(false);
    }
  };

  const handleGenerateArticle = async (post: StrategyPost) => {
    setArticleError(null);
    setGeneratingArticleId(post.id);
    try {
      const res = await generateArticleFromBrief(
        post.title,
        post.target_keyword,
        strategy?.strategy_overview || '',
        currentUser.aiAssistantLanguage || currentUser.preferredLanguage || 'Darija/French'
      );
      const article: GeneratedArticle = {
        id: post.id,
        title: res.title || post.title,
        main_keyphrase: res.main_keyphrase || res.target_keyword,
        seo_title: res.seo_title,
        meta_description: res.meta_description,
        slug: res.slug,
        target_keyword: res.target_keyword || post.target_keyword,
        // Prefer Elementor-ready HTML if available, otherwise fall back to raw content
        content: res.elementor_html || res.content,
        language: res.language,
        credits_used: res.credits_used || 0,
        keyword_density: res.keyword_density,
        image_suggestions: res.image_suggestions || [],
      };
      setArticles(prev => ({ ...prev, [post.id]: article }));
    } catch (err: any) {
      console.error('Article generation error:', err);
      setArticleError(err.message || (t('strategy.articleErrorGeneric') || 'Failed to generate article. Please check your credits and try again.'));
    } finally {
      setGeneratingArticleId(null);
    }
  };

  const remainingFreeArticlesInfo = !isPremium
    ? t('strategy.freePlanInfo', { credits: String(currentUser.credits) }) ||
      `Free plan: you can generate up to 3 articles using your remaining credits (${currentUser.credits}).`
    : t('strategy.paidPlanInfo') ||
      'Paid plan: you can generate up to 10 articles per week without using credits.';

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 className="text-3xl font-bold text-white flex items-center gap-2">
            <Calendar className="w-7 h-7 text-indigo-400" />
            {t('strategy.title') || 'SEO Strategy & Content Calendar'}
          </h2>
          <p className="text-slate-400 mt-2 max-w-2xl">
            {t('strategy.subtitle') || 'Generate a complete SEO strategy, content calendar, and ready-to-use articles based on your niche and plan.'}
          </p>
        </div>
        <div className="bg-slate-800/60 border border-slate-700 rounded-2xl px-5 py-3 flex items-center gap-4">
          <div className="p-2 rounded-xl bg-indigo-500/20">
            <Target className="w-5 h-5 text-indigo-400" />
          </div>
          <div>
            <div className="text-xs uppercase text-slate-400 tracking-wider">
              {t('strategy.planLabel') || 'Plan'}
            </div>
            <div className="text-sm font-semibold text-white capitalize">
              {currentUser.plan}
            </div>
            <div className="text-[11px] text-slate-500">
              {remainingFreeArticlesInfo}
            </div>
          </div>
        </div>
      </div>

      {/* Input Card */}
      <div className="bg-gradient-to-br from-slate-900/90 via-slate-800/80 to-slate-900/90 border border-slate-700/50 rounded-2xl p-6 md:p-8 flex flex-col md:flex-row gap-6 backdrop-blur-xl">
        <div className="flex-1 space-y-4">
          <label className="block text-sm font-semibold text-white">
            {t('strategy.topicLabel') || 'Your Niche / Main Topic'}
          </label>
          <textarea
            className="w-full bg-slate-950/80 border border-slate-600/50 rounded-xl px-4 py-3 text-sm text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/60 focus:border-indigo-500/50 min-h-[80px]"
            placeholder={t('strategy.topicPlaceholder') || 'Example: Local SEO strategy for a dental clinic in your city'}
            value={topic}
            onChange={e => setTopic(e.target.value)}
          />

          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
            <div className="flex items-center gap-3">
              <MapPin className="w-4 h-4 text-indigo-400" />
              <span className="text-xs text-slate-300">
                {t('strategy.marketLabel') || 'Target Market'}:{' '}
                <span className="font-semibold text-white">
                  {currentUser.preferredCountry || 'United States'}
                </span>
              </span>
            </div>
            <div className="flex items-center gap-3">
              <Globe2 className="w-4 h-4 text-indigo-400" />
              <span className="text-xs text-slate-300">
                {t('strategy.languageLabel') || 'AI Content Language'}:{' '}
                <span className="font-semibold text-white">
                  {currentUser.aiAssistantLanguage || currentUser.preferredLanguage || 'Darija/French'}
                </span>
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-300">
                {t('strategy.weeksLabel') || 'Strategy Duration (weeks)'}:
              </span>
              <input
                type="number"
                min={1}
                max={12}
                value={weeks}
                onChange={e => setWeeks(Math.max(1, Math.min(12, Number(e.target.value) || 1)))}
                className="w-16 bg-slate-950/80 border border-slate-600/50 rounded-lg px-2 py-1 text-xs text-white focus:outline-none focus:ring-1 focus:ring-indigo-500/60 focus:border-indigo-500/50"
              />
            </div>
          </div>

          {strategyError && (
            <div className="mt-2 flex items-start gap-2 text-xs text-red-400 bg-red-500/10 border border-red-500/40 rounded-lg px-3 py-2">
              <AlertCircle className="w-4 h-4 mt-[2px]" />
              <span>{strategyError}</span>
            </div>
          )}
        </div>

        <div className="w-full sm:w-auto sm:min-w-[220px] flex flex-col justify-between gap-4">
          <div className="bg-slate-950/80 border border-slate-600/50 rounded-2xl p-4 space-y-2">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-yellow-400" />
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-300">
                {t('strategy.whatYouGet') || 'What you get'}
              </span>
            </div>
            <ul className="text-xs text-slate-200 space-y-1 list-disc list-inside">
              <li>{t('strategy.benefit1') || 'Full SEO content strategy overview'}</li>
              <li>{t('strategy.benefit2') || 'Weekly publishing calendar'}</li>
              <li>{t('strategy.benefit3') || 'Article briefs ready to generate content'}</li>
              <li>{t('strategy.benefit4') || 'Articles generated in your chosen language'}</li>
            </ul>
          </div>

          <button
            onClick={handleGenerateStrategy}
            disabled={loadingStrategy}
            className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-indigo-600 hover:bg-indigo-500 disabled:bg-slate-700 disabled:cursor-not-allowed text-white text-sm font-semibold py-3 shadow-lg shadow-indigo-500/30 transition-colors"
          >
            {loadingStrategy ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                {t('strategy.generating') || 'Generating strategy...'}
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4" />
                {t('strategy.generateButton') || 'Generate Strategy & Calendar'}
              </>
            )}
          </button>
        </div>
      </div>

      {/* Strategy & Calendar */}
      {strategy ? (
        <div className="space-y-8">
          {/* Overview */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 bg-slate-900/70 border border-slate-700 rounded-2xl p-6">
              <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                <Target className="w-5 h-5 text-indigo-400" />
                {t('strategy.overviewTitle') || 'Strategy Overview'}
              </h3>
              <p className="text-sm text-slate-300 whitespace-pre-line leading-relaxed">
                {strategy.strategy_overview}
              </p>
            </div>
            <div className="bg-slate-900/70 border border-slate-700 rounded-2xl p-6 space-y-3">
              <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                <FileText className="w-4 h-4 text-indigo-400" />
                {t('strategy.metaInfoTitle') || 'Publishing Plan'}
              </h3>
              <div className="text-xs text-slate-300 space-y-1">
                <p>
                  <span className="font-semibold text-slate-200">
                    {t('strategy.planTypeLabel') || 'Plan'}:
                  </span>{' '}
                  <span className="capitalize">{strategy.plan_type}</span>
                </p>
                <p>
                  <span className="font-semibold text-slate-200">
                    {t('strategy.durationLabel') || 'Duration'}:
                  </span>{' '}
                  {strategy.total_weeks || weeks} {t('strategy.weeksShort') || 'weeks'}
                </p>
                <p>
                  <span className="font-semibold text-slate-200">
                    {t('strategy.frequencyLabel') || 'Posting frequency'}:
                  </span>{' '}
                  {strategy.posting_frequency}
                </p>
                <p>
                  <span className="font-semibold text-slate-200">
                    {t('strategy.maxPerWeekLabel') || 'Max articles / week'}:
                  </span>{' '}
                  {strategy.max_articles_per_week}
                </p>
              </div>
              <div>
                <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1">
                  {t('strategy.keywordsLabel') || 'Suggested Keywords'}
                </h4>
                <div className="flex flex-wrap gap-2">
                  {strategy.suggested_keywords?.length ? (
                    strategy.suggested_keywords.slice(0, 12).map((kw, idx) => (
                      <span
                        key={idx}
                        className="inline-flex items-center px-2 py-1 rounded-full bg-slate-800/70 border border-slate-700 text-[11px] text-slate-200"
                      >
                        #{kw}
                      </span>
                    ))
                  ) : (
                    <span className="text-xs text-slate-500">
                      {t('strategy.noKeywords') || 'No keywords detected.'}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Calendar & Articles */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Calendar */}
            <div className="lg:col-span-2 bg-slate-900/70 border border-slate-700 rounded-2xl p-6">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-indigo-400" />
                {t('strategy.calendarTitle') || 'Content Calendar'}
              </h3>
              <div className="space-y-4">
                {strategy.calendar?.map(week => (
                  <div
                    key={week.week}
                    className="border border-slate-800 rounded-xl p-4 bg-slate-950/40"
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">
                          {t('strategy.weekLabel', { week: String(week.week) }) || `Week ${week.week}`}
                        </span>
                        <span className="text-[11px] text-slate-500">
                          {week.focus}
                        </span>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {week.posts?.map(post => {
                        const article = articles[post.id];
                        const isGenerating = generatingArticleId === post.id;
                        return (
                          <div
                            key={post.id}
                            className="border border-slate-800 rounded-lg p-3 bg-slate-900/70 flex flex-col gap-2"
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div>
                                <div className="text-[11px] uppercase tracking-wider text-slate-500">
                                  {post.day} • {post.content_type}
                                </div>
                                <div className="text-sm font-semibold text-slate-100 line-clamp-2">
                                  {post.title}
                                </div>
                                <div className="text-[11px] text-slate-400 mt-1">
                                  {t('strategy.keywordBadge') || 'Keyword'}:{' '}
                                  <span className="text-slate-200 font-medium">
                                    {post.target_keyword}
                                  </span>
                                </div>
                              </div>
                              {article && (
                                <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                              )}
                            </div>
                            <button
                              onClick={() => handleGenerateArticle(post)}
                              disabled={isGenerating || !!article}
                              className={`mt-1 inline-flex items-center justify-center gap-2 rounded-lg px-3 py-1.5 text-[11px] font-semibold ${
                                article
                                  ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40'
                                  : 'bg-indigo-600 text-white hover:bg-indigo-500 border border-indigo-500/60 disabled:bg-slate-700 disabled:border-slate-700 disabled:text-slate-400'
                              }`}
                            >
                              {isGenerating ? (
                                <>
                                  <Loader2 className="w-3 h-3 animate-spin" />
                                  {t('strategy.generatingArticle') || 'Generating...'}
                                </>
                              ) : article ? (
                                <>
                                  <CheckCircle2 className="w-3 h-3" />
                                  {t('strategy.articleReadyButton') || 'Article Ready'}
                                </>
                              ) : (
                                <>
                                  <FileText className="w-3 h-3" />
                                  {t('strategy.generateArticle') || 'Generate Article'}
                                </>
                              )}
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Generated Articles */}
            <div className="bg-slate-900/70 border border-slate-700 rounded-2xl p-6 space-y-3">
              <h3 className="text-lg font-semibold text-white mb-1 flex items-center gap-2">
                <FileText className="w-5 h-5 text-indigo-400" />
                {t('strategy.articlesTitle') || 'Generated Articles'}
              </h3>
              <p className="text-xs text-slate-400 mb-3">
                {t('strategy.articlesSubtitle') || 'Click on a post in the calendar to generate a full article. Content is ready to copy & paste.'}
              </p>

              {articleError && (
                <div className="flex items-start gap-2 text-xs text-red-400 bg-red-500/10 border border-red-500/40 rounded-lg px-3 py-2 mb-2">
                  <AlertCircle className="w-4 h-4 mt-[2px]" />
                  <span>{articleError}</span>
                </div>
              )}

              {Object.keys(articles).length === 0 ? (
                <div className="text-xs text-slate-500 bg-slate-900/60 border border-slate-800 rounded-lg px-3 py-3">
                  {t('strategy.noArticlesYet') || 'No articles generated yet. Use the calendar to generate your first article.'}
                </div>
              ) : (
                <div className="space-y-4 max-h-[520px] overflow-y-auto pr-1">
                  {Object.values(articles).map(article => (
                    <div
                      key={article.id}
                      className="border border-slate-800 rounded-lg p-3 bg-slate-950/60 space-y-2"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1">
                          <div className="text-[11px] uppercase tracking-wider text-emerald-400 flex items-center gap-1">
                            <CheckCircle2 className="w-3 h-3" />
                            {t('strategy.articleReadyLabel') || 'Article Ready'}
                          </div>
                          <div className="text-sm font-semibold text-slate-100 mb-2">
                            {article.title}
                          </div>
                          
                          {/* SEO Fields */}
                          <div className="space-y-2 mb-3 p-2 bg-slate-900/60 rounded-lg border border-slate-700/50">
                            {article.main_keyphrase && (
                              <div className="text-[10px]">
                                <span className="text-slate-400">Main Keyphrase:</span>{' '}
                                <span className="text-emerald-300 font-semibold">{article.main_keyphrase}</span>
                                {article.keyword_density && (
                                  <span className="text-slate-500 ml-1">({article.keyword_density}% density)</span>
                                )}
                              </div>
                            )}
                            {article.seo_title && (
                              <div className="text-[10px]">
                                <span className="text-slate-400">SEO Title:</span>{' '}
                                <span className="text-slate-200">{article.seo_title}</span>
                                <span className="text-slate-500 ml-1">({article.seo_title.length} chars)</span>
                              </div>
                            )}
                            {article.meta_description && (
                              <div className="text-[10px]">
                                <span className="text-slate-400">Meta Description:</span>{' '}
                                <span className="text-slate-200">{article.meta_description}</span>
                                <span className="text-slate-500 ml-1">({article.meta_description.length} chars)</span>
                              </div>
                            )}
                            {article.slug && (
                              <div className="text-[10px]">
                                <span className="text-slate-400">Slug:</span>{' '}
                                <span className="text-slate-300 font-mono">{article.slug}</span>
                              </div>
                            )}
                          </div>
                          
                          <div className="text-[11px] text-slate-500">
                            {t('strategy.keywordBadge') || 'Keyword'}:{' '}
                            <span className="text-slate-300">{article.target_keyword}</span>
                            {' • '}
                            {t('strategy.languageBadge') || 'Language'}:{' '}
                            <span className="text-slate-300">{article.language}</span>
                          </div>
                        </div>
                      </div>
                      <textarea
                        className="w-full bg-slate-900/80 border border-slate-700 rounded-lg px-3 py-2 text-[11px] text-slate-100 min-h-[160px] leading-relaxed font-mono"
                        value={article.content}
                        readOnly
                        onFocus={e => e.currentTarget.select()}
                        placeholder="Article HTML will appear here..."
                      />
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(article.content);
                        }}
                        className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-xs text-slate-100 py-1.5 border border-slate-700"
                      >
                        {t('strategy.copyArticle') || 'Copy Article'}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        !loadingStrategy && (
          <div className="bg-gradient-to-br from-slate-900/90 via-slate-800/80 to-slate-900/90 border border-slate-700/50 rounded-2xl p-6 flex items-start gap-4 backdrop-blur-xl">
            <div className="p-3 rounded-xl bg-indigo-500/20">
              <Calendar className="w-6 h-6 text-indigo-400" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-white mb-1">
                {t('strategy.emptyTitle') || "No strategy yet - let's create one"}
              </h3>
              <p className="text-xs text-slate-300 mb-2">
                {t('strategy.emptyDescription') || 'Describe your business, niche, or main topic above and click "Generate Strategy & Calendar".'}
              </p>
            </div>
          </div>
        )
      )}
    </div>
  );
};


